import { toast } from "sonner";
import { DownloadSimple, DeviceMobile, DeviceTablet, Desktop, AppleLogo, AndroidLogo, ShareNetwork, Package, FileZip } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const PAQ_PACKAGES = [
  { key: "paqway", app: "PAQ.WAY", color: "#F43F5E", file: "PAQ_Connessione_PaqWay_190926.zip",
    desc: "Pacchetto di collegamento completo: SSO con auto-provision, widget VIKI, backup v2, KPI vendite e configurazione SMTP" },
  { key: "sohelp", app: "So.Help", color: "#10B981", file: "PAQ_Connessione_SoHelp_170926.zip",
    desc: "Pacchetto di collegamento completo: SSO con auto-provision, widget VIKI, backup v2 e KPI helpdesk" },
  { key: "produzione", app: "Produzione", color: "#9F1239", file: "PAQ_Aggiornamento_BackupCodice_Produzione_170926.zip",
    desc: "Aggiornamento backup v2: include il codice sorgente nel backup notturno" },
  { key: "contabilita", app: "Contabilità", color: "#38BDF8", file: "PAQ_Aggiornamento_BackupCodice_Contabilita_170926.zip",
    desc: "Aggiornamento backup v2: include il codice sorgente nel backup notturno" },
  { key: "risorice", app: "Risorice", color: "#E05E3F", file: "PAQ_Aggiornamento_BackupCodice_Risorice_170926.zip",
    desc: "Aggiornamento backup v2: include il codice sorgente nel backup notturno" },
  { key: "postaai", app: "Posta AI", color: "#F97316", file: "PAQ_Aggiornamento_BackupCodice_PostaAI_170926.zip",
    desc: "Aggiornamento backup v2 (email e allegati restano esclusi: si re-importano da IMAP)" },
];

const DEVICES = [
  {
    icon: Desktop, title: "PC e Mac (Chrome / Edge)", testId: "install-desktop",
    steps: ["Apri www.areapaq.it con Chrome o Edge", "Clicca l'icona di installazione nella barra degli indirizzi (monitor con freccia) oppure usa il pulsante qui sotto", "Conferma \"Installa\": AREAPAQ si apre in una finestra dedicata e appare tra le tue app"],
  },
  {
    icon: AndroidLogo, title: "Android (Chrome)", testId: "install-android",
    steps: ["Apri www.areapaq.it con Chrome", "Tocca il pulsante qui sotto, oppure menu ⋮ → \"Aggiungi a schermata Home\" / \"Installa app\"", "L'icona AREAPAQ appare nella schermata Home come una vera app"],
  },
  {
    icon: AppleLogo, title: "iPhone e iPad (Safari)", testId: "install-ios",
    steps: ["Apri www.areapaq.it con Safari", "Tocca il pulsante Condividi (quadrato con freccia in su)", "Scorri e scegli \"Aggiungi alla schermata Home\", poi \"Aggiungi\""],
    note: "Su iPhone/iPad l'installazione è possibile solo da Safari: il pulsante automatico non è disponibile.",
  },
  {
    icon: DeviceTablet, title: "Tablet Android", testId: "install-tablet",
    steps: ["Stessa procedura di Android: Chrome → menu ⋮ → \"Installa app\"", "L'app si adatta automaticamente allo schermo del tablet"],
  },
];

export default function InstallPage() {
  const { user } = useAuth();
  const isPrivileged = ["master", "admin"].includes(user?.role);
  const handleInstall = async () => {
    const deferred = window.__deferredInstallPrompt;
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        toast.success("AREAPAQ installata! La trovi tra le tue app");
        window.__deferredInstallPrompt = null;
      }
    } else if (window.matchMedia("(display-mode: standalone)").matches) {
      toast.info("AREAPAQ è già installata su questo dispositivo");
    } else {
      toast.info("Installazione automatica non disponibile su questo browser: segui le istruzioni del tuo dispositivo qui sotto");
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="install-page">
      <div className="mb-10 animate-fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">App su ogni dispositivo</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Scarica WebApp</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl">
          AREAPAQ è una Progressive Web App: si installa direttamente dal browser, senza App Store, e funziona come un'app nativa su PC, tablet e smartphone — con icona, schermo intero e avvio rapido.
        </p>
        <Button data-testid="install-now-button" onClick={handleInstall}
          className="mt-6 h-12 px-8 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
          <DownloadSimple size={18} className="mr-2" /> Installa su questo dispositivo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {DEVICES.map((d, i) => {
          const Icon = d.icon;
          return (
            <div key={d.title} data-testid={d.testId}
              className="p-7 rounded-lg bg-surface border border-surface-border animate-fade-up"
              style={{ animationDelay: `${i * 70}ms` }}>
              <Icon size={32} weight="duotone" className="text-sky-400 mb-3" />
              <h2 className="font-heading text-xl font-semibold tracking-tight mb-3">{d.title}</h2>
              <ol className="space-y-2 text-sm text-slate-400 list-none">
                {d.steps.map((s, j) => (
                  <li key={j} className="flex gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-white/10 text-white/70 text-[11px] font-semibold flex items-center justify-center mt-0.5">{j + 1}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
              {d.note && <p className="text-[11px] text-amber-300/80 mt-3 flex items-start gap-1.5"><ShareNetwork size={13} className="shrink-0 mt-0.5" /> {d.note}</p>}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-white/35 mt-8 flex items-center gap-1.5">
        <DeviceMobile size={13} /> Una volta installata, l'app si aggiorna da sola a ogni nuova versione del portale: non serve reinstallarla.
      </p>

      {isPrivileged && (
        <section className="mt-14 animate-fade-up" data-testid="paq-packages-section">
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Riservato a Master e Admin</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <Package size={28} weight="duotone" className="text-emerald-400" /> Pacchetti PAQ per le app
          </h2>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">
            Un clic e il download parte subito: consegna lo zip all'agente della webapp insieme alle istruzioni contenute nel pacchetto.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {PAQ_PACKAGES.map((p, i) => (
              <a key={p.key} href={`/${p.file}`} download={p.file} data-testid={`paq-package-${p.key}`}
                className="group p-5 rounded-lg bg-surface border border-surface-border hover:border-white/25 transition-colors animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
                    <span className="font-heading text-base font-semibold tracking-tight">{p.app}</span>
                  </span>
                  <DownloadSimple size={18} className="text-white/40 group-hover:text-emerald-400 transition-colors" />
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{p.desc}</p>
                <p className="text-[10.5px] text-white/30 mt-3 flex items-center gap-1.5 break-all">
                  <FileZip size={12} className="shrink-0" /> {p.file}
                </p>
              </a>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
